import { useState, useEffect } from 'react';
import { getMyOrganization, getJoinRequests, approveJoinRequest, rejectJoinRequest } from '../../features/organizations/api';
import type { Organization, JoinRequest } from '../../features/organizations/types';
import { Button } from '../../components/common/Button';
import { Label } from '../../components/common/Label';
import { formatDateTime as formatDate } from '../../utils/formatters';
import '../../components/common/CommonTable.css';
import '../../components/common/CommonFilters.css';
import '../../styles/buttons.css';
import './JoinRequestsPage.css';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

export default function JoinRequestsPage() {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');

  // 却下ダイアログ
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<JoinRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // 承認ダイアログ
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvedRole, setApprovedRole] = useState<'user' | 'admin'>('user');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (organization) {
      loadJoinRequests();
    }
  }, [statusFilter, organization]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const org = await getMyOrganization();
      setOrganization(org);
    } catch (err: any) {
      console.error('Failed to load organization:', err);
      setError(err.message || '組織情報の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const loadJoinRequests = async () => {
    if (!organization) return;

    try {
      setError('');
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const response = await getJoinRequests(organization.id, status);
      setJoinRequests(response.join_requests || []);
    } catch (err: any) {
      console.error('Failed to load join requests:', err);
      setError(err.message || '参加依頼の読み込みに失敗しました');
      setJoinRequests([]);
    }
  };

  const handleApproveClick = (request: JoinRequest) => {
    setSelectedRequest(request);
    // デフォルトは希望権限に設定
    setApprovedRole(request.requested_role);
    setShowApproveDialog(true);
  };

  const handleApproveConfirm = async () => {
    if (!organization || !selectedRequest) return;

    try {
      setIsProcessing(true);
      await approveJoinRequest(organization.id, selectedRequest.id, {
        approved_role: approvedRole,
      });

      // ダイアログを閉じる
      setShowApproveDialog(false);
      setSelectedRequest(null);

      // リストを更新
      await loadJoinRequests();

      alert('参加依頼を承認しました');
    } catch (err: any) {
      console.error('Failed to approve join request:', err);
      // サーバー側のエラーメッセージを優先して表示（axios の汎用文言では原因が分からない）
      alert(err.response?.data?.error || err.message || '承認に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectClick = (request: JoinRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!organization || !selectedRequest) return;

    try {
      setIsProcessing(true);
      await rejectJoinRequest(organization.id, selectedRequest.id, {
        rejection_reason: rejectionReason.trim() || undefined,
      });

      // ダイアログを閉じる
      setShowRejectDialog(false);
      setSelectedRequest(null);
      setRejectionReason('');

      // リストを更新
      await loadJoinRequests();

      alert('参加依頼を却下しました');
    } catch (err: any) {
      console.error('Failed to reject join request:', err);
      alert(err.response?.data?.error || err.message || '却下に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page admin-page">
        <div className="admin-loading">データを読み込み中...</div>
      </div>
    );
  }

  if (error && !organization) {
    return (
      <div className="page admin-page">
        <div className="admin-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/single_neutral_actions_check_1.svg" alt="" className="page-title-icon" />
            参加依頼管理
          </h1>
          <p className="page-description">
            {organization?.display_name || '組織'} への参加依頼を管理します
          </p>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {/* フィルター */}
      <section className="list-controls">
        <div className="controls-row">
          <div className="filter-section">
            <img src="/filter_sort_lines_descending.svg" alt="" className="filter-icon" />
            <span className="filter-label">フィルタ</span>
            <div className="filter-group">
              <label htmlFor="status-filter">ステータス</label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="form-select"
              >
                <option value="all">すべて</option>
                <option value="pending">承認待ち</option>
                <option value="approved">承認済み</option>
                <option value="rejected">却下済み</option>
              </select>
            </div>
            <div className="total-count">
              全 <strong>{joinRequests.length}</strong> 件
            </div>
          </div>
        </div>
      </section>

      {/* 参加依頼テーブル */}
      {!joinRequests || joinRequests.length === 0 ? (
        <div className="admin-empty-state">
          <div className="admin-empty-state-title">参加依頼はありません</div>
        </div>
      ) : (
        <div className="table-container">
          <table className="join-requests-table common-table">
            <thead>
              <tr>
                <th className="col-user">ユーザー</th>
                <th className="col-role">希望権限</th>
                <th className="col-message">メッセージ</th>
                <th className="col-status">ステータス</th>
                <th className="col-date">申請日時</th>
                <th className="col-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              {joinRequests.map((request) => (
                <tr key={request.id}>
                  <td className="col-user">
                    <div className="user-cell">
                      {request.picture_url && (
                        <img
                          src={request.picture_url}
                          alt={request.display_name || request.email}
                          className="user-avatar"
                        />
                      )}
                      <div className="user-info">
                        <div className="user-name">
                          {request.display_name || request.email}
                        </div>
                        <div className="user-email">{request.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="col-role">
                    {request.requested_role === 'admin' ? '管理者' : 'ユーザー'}
                    {request.status === 'approved' && request.approved_role && request.approved_role !== request.requested_role && (
                      <div className="approved-role">
                        （付与: {request.approved_role === 'admin' ? '管理者' : 'ユーザー'}）
                      </div>
                    )}
                  </td>
                  <td className="col-message">
                    {request.message ? (
                      <div className="message-preview" title={request.message}>
                        {request.message.length > 50 ? `${request.message.substring(0, 50)}...` : request.message}
                      </div>
                    ) : (
                      <span className="no-message">-</span>
                    )}
                  </td>
                  <td className="col-status">
                    <Label variant={
                      request.status === 'approved' ? 'green' :
                      request.status === 'rejected' ? 'red' : 'yellow'
                    }>
                      {request.status === 'pending' && '承認待ち'}
                      {request.status === 'approved' && '承認済み'}
                      {request.status === 'rejected' && '却下済み'}
                    </Label>
                    {request.status === 'rejected' && request.rejection_reason && (
                      <div className="rejection-reason-hint" title={request.rejection_reason}>
                        理由あり
                      </div>
                    )}
                  </td>
                  <td className="col-date">
                    {formatDate(request.created_at)}
                    {request.reviewed_at && (
                      <div className="reviewed-date">
                        {request.status === 'approved' ? '承認' : '却下'}: {formatDate(request.reviewed_at)}
                      </div>
                    )}
                  </td>
                  <td className="col-actions">
                    {request.status === 'pending' && (
                      <div className="actions-group">
                        <Button
                          variant="success"
                          size="sm"
                          className="btn-modal-sub"
                          onClick={() => handleApproveClick(request)}
                          disabled={isProcessing}
                        >
                          承認
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRejectClick(request)}
                          disabled={isProcessing}
                        >
                          却下
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 承認ダイアログ */}
      {showApproveDialog && selectedRequest && (
        <div className="dialog-overlay" onClick={() => !isProcessing && setShowApproveDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="dialog-title">参加依頼を承認</h2>
            <p className="dialog-description">
              {selectedRequest.email} の参加依頼を承認します。
            </p>

            <div className="form-group">
              <label className="form-label">
                付与する権限
              </label>
              <div className="info-box">
                <strong>希望権限:</strong> {selectedRequest.requested_role === 'admin' ? '管理者' : 'ユーザー'}
              </div>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="approved_role"
                    value="user"
                    checked={approvedRole === 'user'}
                    onChange={(e) => setApprovedRole(e.target.value as 'user' | 'admin')}
                    disabled={isProcessing}
                  />
                  <span className="radio-text">
                    <strong>ユーザー</strong> - 画像生成などの一般的なサービス利用。接続済みAPIを通じたデータ取得・更新は可能。
                  </span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="approved_role"
                    value="admin"
                    checked={approvedRole === 'admin'}
                    onChange={(e) => setApprovedRole(e.target.value as 'user' | 'admin')}
                    disabled={isProcessing}
                  />
                  <span className="radio-text">
                    <strong>管理者</strong> - ユーザー管理、APIの新規接続、チャージ、プラン変更も含めた全機能を利用可能。
                  </span>
                </label>
              </div>
            </div>

            <div className="dialog-actions">
              <button
                className="btn-modal btn-modal-sub"
                onClick={handleApproveConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? '承認中...' : '承認する'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowApproveDialog(false)}
                disabled={isProcessing}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 却下ダイアログ */}
      {showRejectDialog && selectedRequest && (
        <div className="dialog-overlay" onClick={() => !isProcessing && setShowRejectDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="dialog-title">参加依頼を却下</h2>
            <p className="dialog-description">
              {selectedRequest.email} の参加依頼を却下します。
            </p>

            <div className="form-group">
              <label htmlFor="rejectionReason" className="form-label">
                却下理由（任意）
              </label>
              <textarea
                id="rejectionReason"
                className="form-textarea"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="却下の理由を入力してください"
                rows={4}
                maxLength={500}
                disabled={isProcessing}
              />
            </div>

            <div className="dialog-actions">
              <button
                className="btn-modal-danger"
                onClick={handleRejectConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? '却下中...' : '却下する'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowRejectDialog(false)}
                disabled={isProcessing}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
